<template>
    <div id="workingHours">
        <div class="container">
            <div class="title">{{ $t("newWizartV2.workingHours") }}</div>
            <div class="content">
                <div :class="mode === '7/24' ? 'w-card active' : 'w-card'" id="1" @click="mode = '7/24'">
                    <div class="full-time">
                        <div class="icon">
                            <div class="icon-wrapper">
                                <DynamicSvg name="FullTime" />
                            </div>
                        </div>
                        <div class="title">
                            7 / 24 <br />
                            {{ $t("newWizartV2.continuousService") }}
                        </div>
                        <div class="description">
                            {{ $t("newWizartV2.continuousServiceDescription") }}
                        </div>
                    </div>
                    <div class="select-button" @click="mode = '7/24'">
                        {{ $t("newWizartV2.vote") }}
                    </div>
                </div>
                <div
                    :class="mode === 'agent-status-based' ? 'w-card active' : 'w-card'"
                    id="2"
                    @click="mode = 'agent-status-based'"
                >
                    <div class="option-time">
                        <div class="icon">
                            <div class="icon-wrapper">
                                <DynamicSvg color="white" name="ManTime" />
                            </div>
                        </div>
                        <div class="title">
                            {{ $t("newWizartV2.representativeWorking") }}
                        </div>
                        <div class="description">
                            {{ $t("newWizartV2.representativeWorkingDescription") }}
                        </div>
                    </div>
                    <div class="select-button" @click="mode = 'agent-status-based'">
                        {{ $t("newWizartV2.vote") }}
                    </div>
                </div>
                <div :class="mode === 'custom' ? 'w-card active' : 'w-card'" id="3" @click="mode = 'custom'">
                    <div class="private-time">
                        <div class="icon">
                            <div class="icon-wrapper">
                                <md-icon style="color: white">update</md-icon>
                            </div>
                        </div>
                        <div class="title">{{ $t("fastWizard.customHours") }}</div>
                        <div class="inputs-wrapper">
                            <div class="local-time">
                                <div>
                                    <span style="display: flex">{{ $t("fastWizard.customHoursTimezone") }}</span>
                                    <a-select
                                        show-search
                                        :placeholder="$t('fastWizard.placeholderSelectTimezone')"
                                        option-filter-prop="children"
                                        :filter-option="filterOption"
                                        @change="changeTimezone"
                                        :value="timezone"
                                    >
                                        <a-select-option :value="tz" v-for="(tz, i) in timezones" :key="i">
                                            {{ tz }}
                                        </a-select-option>
                                    </a-select>
                                </div>
                            </div>
                            <div v-for="rule in rules" class="ruleItem">
                                <div class="options">
                                    <div class="days">
                                        <span class="display-flex">{{ $t('message.selectDays') }}:</span>
                                        <a-select
                                            show-search
                                            :placeholder="$t('fastWizard.placeholderSelectDays')"
                                            option-filter-prop="children"
                                            :filter-option="filterOption"
                                            @change="(e) => handleDayChange(e, rule._id)"
                                            :value="daysHashcode(rule.days)"
                                        >
                                            <a-select-option
                                                :value="daysHashcode(day.value)"
                                                v-for="(day, i) in days"
                                                :key="i"
                                            >
                                                {{ day.label[$i18n.locale] }}
                                            </a-select-option>
                                        </a-select>
                                    </div>
                                    <div class="option">
                                        <span class="display-flex">{{ $t("fastWizard.customHoursFrom") }}</span>
                                        <a-select
                                            show-search
                                            :placeholder="$t('fastWizard.placeholderSelectStartHour')"
                                            option-filter-prop="children"
                                            :filter-option="filterOption"
                                            @change="(e) => handleFromHourChange(e, rule._id)"
                                            :value="rule.from"
                                        >
                                            <a-select-option :value="hour" v-for="(hour, i) in hours" :key="i">
                                                {{ hour }}
                                            </a-select-option>
                                        </a-select>
                                    </div>
                                    <div class="option">
                                        <span class="display-flex">{{ $t("fastWizard.customHoursTo") }}</span>
                                        <a-select
                                            show-search
                                            :placeholder="$t('fastWizard.placeholderSelectEndHour')"
                                            option-filter-prop="children"
                                            :filter-option="filterOption"
                                            @change="(e) => handleEndHourChange(e, rule._id)"
                                            :value="rule.to"
                                        >
                                            <a-select-option :value="hour" v-for="(hour, i) in hours" :key="i">
                                                {{ hour }}
                                            </a-select-option>
                                        </a-select>
                                    </div>
                                    <div class="delete-button" @click="deleteHour(rule._id)" v-if="rules.length > 1">
                                        <md-icon> delete </md-icon>
                                    </div>
                                </div>
                            </div>
                            <div class="add-hour-button" @click="addHour">{{ $t('message.addTime') }}</div>
                        </div>
                    </div>
                    <div class="select-button" @click="mode = 'custom'">
                        {{ $t("newWizartV2.vote") }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import log2 from "../../utils/log2";
import { mapActions, mapGetters, mapState } from "vuex";
import { sendSocketMessage } from "../../utils/socketStream";

import _ from "lodash";
import moment from "moment-timezone";
import ActionDelete from "../svg/ActionDelete";
import { getNavigatorTimezone } from "../../utils/language";
import DynamicSvg from "../common/DynamicSvg.vue";

const log = log2("AvailabilityConfig");
const defaultRules = [
    {
        days: [1, 2, 3, 4, 5],
        from: "09:00",
        to: "17:00",
    },
];
const momentTimezoneList = moment.tz.names();
const defaultTZ =
    momentTimezoneList.find(
        (x) => x.trim().toLowerCase().indexOf(getNavigatorTimezone().timeZone.trim().toLowerCase()) !== -1
    ) || "UTC";
log("defaultTZ", defaultTZ, momentTimezoneList, getNavigatorTimezone());
export default {
    name: "AvailabilityConfig",
    components: { DynamicSvg, ActionDelete },
    data() {
        return {
            mode: "agent-status-based",
            timezone: null,
            rules: defaultRules,
            timezones: momentTimezoneList,
            hours: [
                ...[...Array(48).keys()].map((v, i) =>
                    moment()
                        .startOf("day")
                        .add(v * 30, "minute")
                        .format("HH:mm")
                ),
                "23:59",
            ],
            days: [
                {
                    label: {
                        en: "Every Day",
                        ru: "Every Day",
                        tr: "Her Gün",
                        de: "Täglich",
                        fr: "Tous les jours",
                        es: "Cada día",
                        ar: "كل يوم",
                    },
                    value: [1, 2, 3, 4, 5, 6, 7],
                },
                {
                    label: {
                        en: "Mon - Fri",
                        ru: "Mon - Fri",
                        tr: "Pzt - Cuma",
                        de: "Mo - Freitag",
                        fr: "Lun - Vendredi",
                        es: "lun - viernes",
                        ar: "الاثنين - الجمعة",
                    },
                    value: [1, 2, 3, 4, 5],
                },
                {
                    label: {
                        en: "Sat - Sun",
                        ru: "Sat - Sun",
                        tr: "Cts - Pzr",
                        de: "Sa - So",
                        fr: "Sam-Dim",
                        es: "Sáb-dom",
                        ar: "اشعة الشمس",
                    },
                    value: [6, 7],
                },
                {
                    label: {
                        en: "Monday",
                        ru: "Monday",
                        tr: "Pazartesi",
                        de: "Montag",
                        fr: "Lundi",
                        es: "Lunes",
                        ar: "الاثنين",
                    },
                    value: [1],
                },
                {
                    label: {
                        en: "Tuesday",
                        ru: "Tuesday",
                        tr: "Salı",
                        de: "Dienstag",
                        fr: "Mardi",
                        es: "Martes",
                        ar: "يوم الثلاثاء",
                    },
                    value: [2],
                },
                {
                    label: {
                        en: "Wednesday",
                        ru: "Wednesday",
                        tr: "Çarşamba",
                        de: "Mittwoch",
                        fr: "Mercredi",
                        es: "Miércoles",
                        ar: "الاربعاء",
                    },
                    value: [3],
                },
                {
                    label: {
                        en: "Thursday",
                        ru: "Thursday",
                        tr: "Perşembe",
                        de: "Donnerstag",
                        fr: "Jeudi",
                        es: "Jueves",
                        ar: "يوم الخميس",
                    },
                    value: [4],
                },
                {
                    label: {
                        en: "Friday",
                        ru: "Friday",
                        tr: "Cuma",
                        de: "Freitag",
                        fr: "Vendredi",
                        es: "Viernes",
                        ar: "جمعة",
                    },
                    value: [5],
                },
                {
                    label: {
                        en: "Saturday",
                        ru: "Saturday",
                        tr: "Cumartesi",
                        de: "Samstag",
                        fr: "Samedi",
                        es: "Sábado",
                        ar: "السبت",
                    },
                    value: [6],
                },
                {
                    label: {
                        en: "Sunday",
                        ru: "Sunday",
                        tr: "Pazar",
                        de: "Sonntag",
                        fr: "Dimanche",
                        es: "Domingo",
                        ar: "الأحد",
                    },
                    value: [7],
                },
            ],
        };
    },
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
            settingInitialized: (state) => state.setting.initialized,
        }),
    },
    methods: {
        ...mapActions({
            getSettings: "setting/getSettings",
            updateSettings: "setting/updateSettings",
            setSettingValue: "setting/setSettingValue",
        }),

        async deleteHour(id) {
            log("deleteHour", id);
            const newHours = this.rules.filter((x) => x._id !== id);
            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.hours",
                value: newHours,
            });
            this.applySettings(newSettings);
        },
        async addHour() {
            const newHours = [...this.rules, defaultRules[0]];
            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.hours",
                value: newHours,
            });
            this.applySettings(newSettings);
        },
        async changeTimezone(timezone) {
            log("changeTimezone", { timezone });

            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.timezone",
                value: timezone,
            });
            this.applySettings(newSettings);
        },
        async handleDayChange(value, ruleId) {
            log("handleDayChange", { value, ruleId });
            const newHours = this.rules.map((x) => {
                return x._id === ruleId
                    ? {
                          ...x,
                          days: this.days.find((day) => this.daysHashcode(day.value) === value).value,
                      }
                    : x;
            });
            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.hours",
                value: newHours,
            });
            this.applySettings(newSettings);
        },
        async handleFromHourChange(value, ruleId) {
            log("handleFromHourChange", { value, ruleId });
            const newHours = this.rules.map((x) => {
                return x._id === ruleId ? { ...x, from: value } : x;
            });
            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.hours",
                value: newHours,
            });
            this.applySettings(newSettings);
        },
        async handleEndHourChange(value, ruleId) {
            log("handleEndHourChange", { value, ruleId });
            const newHours = this.rules.map((x) => {
                return x._id === ruleId ? { ...x, to: value } : x;
            });
            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.hours",
                value: newHours,
            });
            this.applySettings(newSettings);
        },
        handleBlur() {
            log("blur");
        },
        handleFocus() {
            log("focus");
        },
        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        daysHashcode(days) {
            return _.orderBy(days, (q) => q, "asc").join("-");
        },
        applySettings(newSettings) {
            log("applySettings", newSettings);
            this.updateSettings(newSettings).then(() => {
                sendSocketMessage("user settings-updated", {
                    siteId: this.site._id,
                });
            });
        },
    },
    watch: {
        settings: {
            handler: function (newSettings) {
                if (_.get(newSettings, "availabilitySettings.mode") !== this.mode) {
                    this.mode = _.get(newSettings, "availabilitySettings.mode");
                }
                if (_.get(newSettings, "availabilitySettings.mode") === "custom") {
                    if (!_.isEqual(this.rules, newSettings.availabilitySettings.hours)) {
                        this.rules = newSettings.availabilitySettings.hours;
                    }
                }
                const tz = _.get(newSettings, "availabilitySettings.timezone");
                if (tz && tz !== this.timezone) {
                    this.timezone = tz;
                }
            },
            deep: true,
        },
        async mode(newMode, oldMode) {
            if (newMode && this.settings) {
                const oldSettings = _.cloneDeep(this.settings);
                const updates = [
                    {
                        setting: "availabilitySettings.mode",
                        value: newMode,
                    },
                ];
                if (newMode === "custom") {
                    if (!oldSettings.availabilitySettings.hours.length) {
                        updates.push({
                            setting: "availabilitySettings.hours",
                            value: defaultRules,
                        });
                    }
                    if (!oldSettings.timezone || oldSettings.timezone === "UTC") {
                        updates.push({
                            setting: "availabilitySettings.timezone",
                            value: defaultTZ,
                        });
                    }
                }
                const newSettings = await this.setSettingValue(updates);
                if (!_.isEqual(oldSettings, newSettings)) this.applySettings(newSettings);
            }
        },
    },
    mounted() {
        const defaultMode = "agent-status-based";
        this.mode = _.get(this.settings, "availabilitySettings.mode", defaultMode);
        this.timezone = _.get(
            this.settings,
            "availabilitySettings.timezone",
            Intl.DateTimeFormat().resolvedOptions().timeZone
        );
    },
};
</script>

<style lang="scss">
#workingHours {
    .container {
        .title {
            font-size: 36px;
            line-height: 54px;
            font-style: normal;
            text-align: center;
            color: #1f1f1f;
            margin-bottom: 30px;
        }

        .content {
            display: grid;
            padding: 0px 30px;
            grid-template-columns: 30% 30% 40%;
            gap: 16px;
            width: 80%;
            margin: 0 auto;
            padding-bottom: 100px;

            .w-card {
                border-radius: 15px;
                text-align: center;
                box-shadow: 10px 24px 27px rgba(0, 0, 0, 0.06);
                font-family: "Poppins-Bold";
                padding: 15px 0px;
                display: flex;
                flex-direction: column;
                justify-content: space-between;

                &:not(:last-child) {
                    margin-right: 24px;
                }

                .icon {
                    display: flex;
                    justify-content: center;

                    .icon-wrapper {
                        background-color: #dddddd;
                        border-radius: 50%;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 50px;
                        height: 50px;
                        padding-top: 5px;
                    }
                }

                .title {
                    font-size: 25px;
                    font-weight: 600;
                    font-style: normal;
                    padding: 0px 10px;
                    margin: 30px;
                    line-height: 35px;
                    color: #1f1f1f;
                }

                .description {
                }

                .select-button {
                    width: 80%;
                    margin: 0 auto;
                    background-color: #dddddd;
                    padding: 10px;
                    border-radius: 7px;
                    align-items: center;
                    font-size: 13px;
                    font-weight: bold;
                    color: #ffffff;
                    margin-top: 50px;
                    margin-bottom: 10px;
                    text-align: center;
                    cursor: pointer;
                    user-select: none;

                    &:hover {
                        background-color: #ec4d1f;
                    }
                }

                .full-time {
                    .description {
                        margin-bottom: 100px;
                    }
                }

                .private-time {
                    .icon-wrapper {
                        margin: 0;
                        padding: 0;

                        i {
                            font-size: 30px !important;
                        }
                    }

                    span {
                        font-size: 13px;
                        font-weight: normal;
                        line-height: 20px;
                        font-style: normal;
                        text-align: left;
                        color: rgba(116, 116, 116, 0.69);
                    }

                    .inputs-wrapper {
                        .local-time {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            width: 100%;

                            span {
                                margin-right: 20px;
                            }
                        }

                        .ruleItem {
                            .options {
                                width: 90%;
                                margin: 10px auto;
                                display: grid;
                                grid-template-columns: auto auto auto auto;

                                .delete-button {
                                    display: flex;
                                    margin-top: 10px;

                                    i {
                                        color: red !important;
                                        cursor: pointer;
                                    }
                                }
                            }

                            .ant-select-selection {
                            }
                        }

                        .add-hour-button {
                            width: 20%;
                            margin: 0 auto;
                            margin-bottom: 80px;
                            margin-top: 25px;
                            padding: 5px;
                            display: flex;
                            justify-content: center;
                            text-align: center;
                            background-color: #dddddd;
                            color: white;
                            border-radius: 5px;
                        }
                    }
                }

                &.active {
                    border: 1px solid #ec4d1f;

                    .select-button {
                        background-color: #ec4d1f;
                    }

                    .add-hour-button {
                        background-color: #ec4d1f !important;
                        cursor: pointer;
                        user-select: none;
                    }

                    .icon-wrapper {
                        background-color: #ec4d1f;
                    }
                }
            }

            .w-card:hover {
                border: 1px solid #ec4d1f;

                .select-button {
                    background-color: #ec4d1f;
                }

                .add-hour-button {
                    background-color: #ec4d1f !important;
                    cursor: pointer;
                    user-select: none;
                }

                .icon-wrapper {
                    background-color: #ec4d1f;
                }
            }
        }
    }
}

.ant-select-selection {
    width: 100% !important;
    height: auto !important;
    border: 1px solid rgba(116, 116, 116, 0.29) !important;
    border-radius: 8px !important;
    background-color: #f6f6f6 !important;
    color: rgba(38, 27, 27, 0.69) !important;

    &:hover {
        border: 1px solid gray !important;
        outline: none;
    }

    &:focus {
    }
}

.ant-select-selection-selected-value {
    margin-top: 0px !important;
}

@media (max-width: 1000px) {
    #workingHours .container .content {
        display: flex;
        flex-direction: column;
        padding: 0px !important;

        .w-card {
            width: 100%;
            margin-right: 0;

            .select-button,
            .add-hour-button {
                width: 100%;
            }

            .icon {
                margin-bottom: 10px;
            }
        }
    }
    #workingHours .container .title {
        font-size: 20px;
        font-weight: 600;
        font-style: normal;
        padding: 0px 10px;
        line-height: 35px;
        color: #1f1f1f;
    }
}

@media (max-width: 900px) {
    #workingHours .container .content {
        display: flex;
        flex-direction: column;

        .w-card {
            width: 100%;
            margin-right: 0;

            .select-button,
            .add-hour-button {
                width: 100%;
            }

            .icon {
                margin-bottom: 10px;
            }
        }
    }
}

@media (max-width: 600px) {
    #workingHours .container .content {
        display: flex;
        flex-direction: column;
        padding-bottom: 20% !important;

        .w-card {
            width: 100%;
            margin-right: 0;

            .select-button,
            .add-hour-button {
                width: 100%;
            }

            .icon {
                margin-bottom: 10px;
            }
        }
    }
    .options {
        display: block !important;
    }
    .add-hour-button {
        width: 80% !important;
    }
    .select-button {
        width: 80% !important;
    }
}
</style>
<style lang="scss" scoped>
.display-flex {
    display: flex;
    justify-content: center;
    font-size: 11px;
}
</style>
